import { Page } from "./common";
import { PageHead } from "../components/ui";
import { Icon } from "../components/Icons";
import { TeamPanel } from "../components/TeamPanel";
import { actions, useStore } from "../lib/store";
import { useSession } from "../lib/auth";
import type { NotifyPrefs } from "../lib/types";

const KINDS: { key: keyof NotifyPrefs; label: string; blurb: string }[] = [
  { key: "clientActivity", label: "Client activity", blurb: "Approvals, brief returns, design responses" },
  { key: "money", label: "Money", blurb: "Invoices, payment claims, variations" },
  { key: "drawings", label: "Drawings", blurb: "Review comments, RFIs, issued sets" },
  { key: "site", label: "Site", blurb: "Progress updates and snags" },
];
const DEFAULTS: NotifyPrefs = { mode: "instant", clientActivity: true, money: true, drawings: true, site: true };

export default function Team() {
  return (
    <Page>
      <PageHead kicker="Studio · People & access" title="Team & roles" />
      <p className="muted reveal" style={{ maxWidth: 620, marginBottom: 22 }}>
        Everyone signs in with their own <b>account</b> (email &amp; password). Share the invite link — new sign-ups appear here for you to assign a role.
      </p>
      <div className="reveal" style={{ marginBottom: 22 }}><MyNotifications /></div>
      <div className="reveal"><TeamPanel /></div>
    </Page>
  );
}

/* Self-service: what reaches your own inbox. Kept per-person rather than
   studio-wide, because a principal and a designer want different noise. */
function MyNotifications() {
  const session = useSession();
  const { notifyPrefs } = useStore();
  const email = (session?.email ?? "").toLowerCase();
  const prefs: NotifyPrefs = (email && notifyPrefs?.[email]) || DEFAULTS;
  const set = (patch: Partial<NotifyPrefs>) => { if (email) actions.setNotifyPrefs(email, { ...prefs, ...patch }); };
  if (!email) return null;

  return (
    <div className="card pad">
      <div className="row between wrap" style={{ gap: 10, marginBottom: 4 }}>
        <div className="row" style={{ gap: 8 }}><Icon.bell width={16} style={{ color: "var(--accent-ink)" }} /><span className="kicker">Your notifications</span></div>
        <span className="dim" style={{ fontSize: 11.5 }}>{email}</span>
      </div>
      <p className="muted" style={{ fontSize: 12.5, marginBottom: 14, maxWidth: 620 }}>
        What reaches your inbox, and how often. This is your own setting — it doesn't change what anyone else receives.
      </p>

      <div className="row wrap" style={{ gap: 6, marginBottom: 14 }}>
        {([["instant", "As it happens"], ["daily", "Once a day"], ["off", "Nothing by email"]] as const).map(([v, label]) => (
          <button key={v} className={"chip" + (prefs.mode === v ? " on" : "")}
            style={{ fontSize: 12, background: prefs.mode === v ? "var(--accent)" : "var(--card)", color: prefs.mode === v ? "#fff" : "var(--ink-2)", borderColor: prefs.mode === v ? "var(--accent)" : "var(--line-2)" }}
            onClick={() => set({ mode: v })}>{label}</button>
        ))}
      </div>

      {prefs.mode !== "off" && (
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(230px,1fr))", gap: 8 }}>
          {KINDS.map((k) => {
            const on = prefs[k.key] !== false;
            return (
              <button key={k.key} className="panel row between" onClick={() => set({ [k.key]: !on } as Partial<NotifyPrefs>)}
                style={{ padding: "10px 12px", cursor: "pointer", textAlign: "left", gap: 10, opacity: on ? 1 : 0.6 }}>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 13, fontWeight: 500 }}>{k.label}</span>
                  <span className="dim" style={{ fontSize: 11 }}>{k.blurb}</span>
                </span>
                <span style={{ width: 36, height: 21, borderRadius: 21, background: on ? "var(--accent)" : "var(--line-2)", position: "relative", transition: "background .2s", flexShrink: 0 }}>
                  <span style={{ position: "absolute", top: 2, left: on ? 17 : 2, width: 17, height: 17, borderRadius: 17, background: "#fff", transition: "left .2s", boxShadow: "0 1px 3px rgba(0,0,0,.25)" }} />
                </span>
              </button>
            );
          })}
        </div>
      )}
      <div className="dim" style={{ fontSize: 11, marginTop: 12, lineHeight: 1.55 }}>
        {prefs.mode === "off"
          ? "You'll still see everything in the cockpit — this only silences email."
          : prefs.mode === "daily"
            ? "Held and sent as one digest each morning, so nothing pings you mid-drawing."
            : "Sent as things happen."}
      </div>
    </div>
  );
}
